import { unzip, type UnzipFileInfo } from 'fflate';

export const MAX_TOTAL_SIZE_BYTES = 1024 * 1024 * 1000;

export const bytesToMegabytes = (bytes: number): number => Math.round(bytes / (1024 * 1024));

export class IntakeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IntakeError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

const JUNK_DIRECTORIES = new Set(['__MACOSX']);
const JUNK_BASENAMES = new Set(
  ['.DS_Store', 'Thumbs.db', 'DICOMDIR'].map(basename => basename.toLowerCase())
);

const STORED = 0;
const DEFLATE = 8;

function isJunkPath(path: string): boolean {
  if (path.endsWith('/')) {
    return true;
  }

  const segments = path.split('/');

  if (segments.some(segment => JUNK_DIRECTORIES.has(segment))) {
    return true;
  }

  const basename = segments[segments.length - 1];

  if (!basename) {
    return true;
  }

  return basename.startsWith('._') || JUNK_BASENAMES.has(basename.toLowerCase());
}

function isZipName(name: string): boolean {
  return name.toLowerCase().endsWith('.zip');
}

function unzipAsync(
  data: Uint8Array,
  filter: (info: UnzipFileInfo) => boolean
): Promise<Record<string, Uint8Array>> {
  return new Promise((resolve, reject) => {
    unzip(data, { filter }, (error, unzipped) => {
      if (error) {
        reject(new IntakeError(`Could not read the archive: ${error.message}`));
        return;
      }

      resolve(unzipped);
    });
  });
}

function isReadable(info: UnzipFileInfo): boolean {
  return info.compression === STORED || info.compression === DEFLATE;
}

async function measureArchive(
  data: Uint8Array
): Promise<{ totalOriginalSize: number; unreadable: number }> {
  let totalOriginalSize = 0;
  let unreadable = 0;

  await unzipAsync(data, (info: UnzipFileInfo) => {
    if (isJunkPath(info.name)) {
      return false;
    }

    if (!isReadable(info)) {
      unreadable += 1;
      return false;
    }

    totalOriginalSize += info.originalSize;
    return false;
  });

  return { totalOriginalSize, unreadable };
}

async function expandArchive(data: Uint8Array): Promise<File[]> {
  const unzipped = await unzipAsync(
    data,
    (info: UnzipFileInfo) => !isJunkPath(info.name) && isReadable(info)
  );

  return Object.entries(unzipped).map(([path, bytes]) => {
    const name = path.split('/').pop() ?? path;

    return new File([bytes], name);
  });
}

function assertUnderCap(totalBytes: number): void {
  if (totalBytes > MAX_TOTAL_SIZE_BYTES) {
    throw new IntakeError(
      `Total upload size must be less than ${bytesToMegabytes(MAX_TOTAL_SIZE_BYTES)}MB`
    );
  }
}

async function measureAndBuffer(
  archives: readonly File[]
): Promise<{ total: number; buffers: Uint8Array[]; unreadable: number }> {
  let total = 0;
  let unreadable = 0;
  const buffers: Uint8Array[] = [];

  for (const archive of archives) {
    const data = new Uint8Array(await archive.arrayBuffer());
    const measured = await measureArchive(data);

    if (measured.unreadable > 0) {
      // Logged as well as returned: the count is what the caller needs to raise a
      // toast, the archive name is diagnosis and has no place in one.
      console.error(
        `[sycai] ${measured.unreadable} entr(ies) in ${archive.name} use a compression ` +
          `method fflate cannot read; skipped`
      );
    }

    total += measured.totalOriginalSize;
    unreadable += measured.unreadable;
    buffers.push(data);
  }

  return { total, buffers, unreadable };
}

export interface IntakeResult {
  files: File[];
  unreadable: number;
}

/**
 * The invariant applies to the RESULT: one drop is one study. Nothing here enforces
 * that — it cannot be read off the bytes — but every consumer assumes it.
 */
export async function expandDropped(dropped: readonly File[]): Promise<IntakeResult> {
  const loose: File[] = [];
  const archives: File[] = [];

  for (const file of dropped) {
    const relativePath = (file as { path?: string }).path || file.webkitRelativePath || file.name;

    if (isJunkPath(relativePath)) {
      continue;
    }

    (isZipName(file.name) ? archives : loose).push(file);
  }

  const looseTotal = loose.reduce((sum, file) => sum + file.size, 0);
  const {
    total: archivesTotal,
    buffers,
    unreadable: archivesUnreadable,
  } = await measureAndBuffer(archives);

  assertUnderCap(looseTotal + archivesTotal);

  const expanded: File[] = [...loose];

  for (const data of buffers) {
    const files = await expandArchive(data);
    expanded.push(...files);
  }

  const nested = expanded.filter(file => isZipName(file.name));

  if (nested.length === 0) {
    return { files: expanded, unreadable: archivesUnreadable };
  }

  // Depth 1, not unbounded recursion.
  const flat = expanded.filter(file => !isZipName(file.name));

  const flatTotal = flat.reduce((sum, file) => sum + file.size, 0);
  const {
    total: nestedTotal,
    buffers: nestedBuffers,
    unreadable: nestedUnreadable,
  } = await measureAndBuffer(nested);

  assertUnderCap(flatTotal + nestedTotal);

  for (const data of nestedBuffers) {
    const inner = await expandArchive(data);
    flat.push(...inner.filter(file => !isZipName(file.name)));
  }

  assertUnderCap(flat.reduce((sum, file) => sum + file.size, 0));

  return { files: flat, unreadable: archivesUnreadable + nestedUnreadable };
}

const DICM = [0x44, 0x49, 0x43, 0x4d];
const PREAMBLE_LENGTH = 128;
const HEADER_LENGTH = PREAMBLE_LENGTH + DICM.length;

/**
 * Four bytes, not a whole-file read to inspect four.
 */
async function hasDicomMagic(file: File): Promise<boolean> {
  if (file.size < HEADER_LENGTH) {
    return false;
  }

  const head = new Uint8Array(await file.slice(PREAMBLE_LENGTH, HEADER_LENGTH).arrayBuffer());

  return DICM.every((byte, index) => head[index] === byte);
}

export async function ensurePreamble(file: File): Promise<File> {
  if (await hasDicomMagic(file)) {
    return file;
  }

  const header = new Uint8Array(HEADER_LENGTH);
  header.set(DICM, PREAMBLE_LENGTH);

  return new File([header, file], file.name, {
    type: file.type,
    lastModified: file.lastModified,
  });
}
